export const CONTACT_METHOD_LABELS = [
  'Main',
  'Sales',
  'Office',
  'Mobile',
  'Emergency',
  'Accounting',
  'Technical',
  'Other',
];

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

export const createContactMethodId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;

  if (
    cryptoApi &&
    typeof cryptoApi.randomUUID === 'function'
  ) {
    return cryptoApi.randomUUID();
  }

  return `contact-method-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createEmptyContactMethod = (overrides = {}) => ({
  id: createContactMethodId(),
  label: 'Main',
  number: '',
  hasWhatsapp: false,
  isPrimary: true,
  ...overrides,
});

const getLegacyNumber = (data = {}, numberKeys = []) => {
  const key = numberKeys.find((fieldName) => normalizeString(data?.[fieldName]));

  return key ? normalizeString(data[key]) : '';
};

const getLegacyWhatsapp = (data = {}, whatsappKeys = []) => {
  const key = whatsappKeys.find((fieldName) => normalizeString(data?.[fieldName]));

  return key ? normalizeString(data[key]) : '';
};

export const normalizeContactMethods = (
  data = {},
  {
    numberKeys = ['companyPhone', 'phone'],
    whatsappKeys = ['companyWhatsapp', 'whatsapp'],
    defaultLabel = 'Main',
    requireAtLeastOne = false,
  } = {}
) => {
  const sourceMethods = Array.isArray(data?.contactMethods)
    ? data.contactMethods
    : [];
  const normalizedMethods = sourceMethods
    .map((method, index) => ({
      id: normalizeString(method?.id) || createContactMethodId(),
      label: normalizeString(method?.label) || (index === 0 ? defaultLabel : 'Other'),
      number: normalizeString(method?.number),
      hasWhatsapp: method?.hasWhatsapp === true || method?.hasWhatsApp === true,
      isPrimary: method?.isPrimary === true,
    }))
    .filter((method) => method.number);

  if (normalizedMethods.length === 0) {
    const legacyNumber = getLegacyNumber(data, numberKeys);
    const legacyWhatsapp = getLegacyWhatsapp(data, whatsappKeys);

    if (legacyNumber) {
      normalizedMethods.push({
        id: createContactMethodId(),
        label: defaultLabel,
        number: legacyNumber,
        hasWhatsapp: Boolean(legacyWhatsapp),
        isPrimary: true,
      });
    }
  }

  if (normalizedMethods.length === 0 && requireAtLeastOne) {
    normalizedMethods.push(createEmptyContactMethod({ label: defaultLabel }));
  }

  const primaryIndex = normalizedMethods.findIndex((method) => method.isPrimary);

  return normalizedMethods.map((method, index) => ({
    ...method,
    isPrimary: primaryIndex >= 0 ? index === primaryIndex : index === 0,
  }));
};

export const sortContactMethods = (methods = []) =>
  [...(Array.isArray(methods) ? methods : [])].sort((first, second) => {
    if (first?.isPrimary === true && second?.isPrimary !== true) {
      return -1;
    }

    if (second?.isPrimary === true && first?.isPrimary !== true) {
      return 1;
    }

    return 0;
  });

export const getPrimaryContactMethod = (methods = []) =>
  sortContactMethods(methods).find((method) => normalizeString(method?.number)) || null;

export const normalizeDialNumber = (number = '') => {
  const digits = String(number).replace(/[^\d+]/g, '');

  if (digits.startsWith('+')) {
    return digits;
  }

  if (digits.startsWith('00')) {
    return `+${digits.slice(2)}`;
  }

  if (digits.startsWith('0')) {
    return `+20${digits.slice(1)}`;
  }

  if (digits.startsWith('20')) {
    return `+${digits}`;
  }

  return digits ? `+${digits}` : '';
};

export const normalizeWhatsappNumber = (number = '') =>
  normalizeDialNumber(number).replace(/^\+/, '');

export const formatContactMethodsForExport = (methods = []) =>
  sortContactMethods(methods)
    .map((method) => {
      const parts = [
        method?.isPrimary ? 'Primary' : '',
        normalizeString(method?.label) || 'Other',
        normalizeString(method?.number),
        method?.hasWhatsapp ? 'WhatsApp' : '',
      ].filter(Boolean);

      return parts.join(' - ');
    })
    .join('\n');
